import { useCallback, useMemo, useState } from "react";
import { Control, FieldValues, Path, useController } from "react-hook-form";
import styled from "@emotion/styled";

import {
  findCpvCode,
  findMatchingCpvCodes,
  getCpvPrefix,
} from "components/form_components/dropdown_list_selects/cpv_code_selects/cpv_code_utils";
import CpvTreeSelect from "components/form_components/dropdown_list_selects/cpv_code_selects/CpvTreeSelect";
import { useCpvTree } from "components/form_components/dropdown_list_selects/cpv_code_selects/useCpvTree";
import Tag from "components/ui/tag/Tag";
import { useDebouncedValue } from "lib/debounce";
import { SignalPreviewCountsByKey } from "lib/hooks/api/signals/useSignalPreviewCounts";
import UIcon from "lib/icons/untitled_ui/UIcon";
import { pluralise, simpleArrayDedupe } from "lib/utils";
import { Flex } from "../../../styles/utility-components";
import ClearAllSignalButton from "./ClearAllSignalButton";
import SignalMatchCount from "./SignalMatchCount";

type CPVCodeInputProps<T extends FieldValues> = {
  name: Path<T>;
  control: Control<T, unknown>;
  resultCounts?: SignalPreviewCountsByKey;
  isLoadingCounts?: boolean;
};

type CpvTag = { label: string; value: string };

function countTooltip(count: number | undefined): string | undefined {
  if (count === undefined) {
    return undefined;
  }
  if (count === 0) {
    return "No matches in the last 6 months";
  }
  return `Matched ${pluralise(count, "notice", true)} in the last 6 months`;
}

/**
 * Signal-settings CPV picker, backed entirely by the app-service `allCpvCodes`
 * query (via `useCpvTree`). Composes the same browse/search dropdown + tags used
 * by the notices/framework CPV filters, minus their filter-only signal chrome
 * (the saved-signals category and "Save as signals" button): here the selected
 * value *is* the CPV signal list, which the signal-settings form saves itself.
 */
export default function CPVCodeInput<T extends FieldValues>({
  name,
  control,
  resultCounts,
  isLoadingCounts = false,
}: CPVCodeInputProps<T>) {
  const { field } = useController({ name, control });
  const { value, onChange } = field;

  const [searchText, setSearchText] = useState("");
  const [debouncedText] = useDebouncedValue(searchText, 300);
  const [isOpen, setIsOpen] = useState(false);

  const selectedCpvCodes = useMemo<string[]>(() => (Array.isArray(value) ? value : []), [value]);

  const { cpvTree, allCpvCodes, isLoading } = useCpvTree([]);

  const filteredCpvCodes = useMemo(() => {
    if (debouncedText.length === 0 || !cpvTree.length) {
      return [];
    }
    return findMatchingCpvCodes(cpvTree, debouncedText, debouncedText.match(/^\d+$/) !== null);
  }, [debouncedText, cpvTree]);

  const displayedCpvTags = useMemo((): CpvTag[] => {
    if (!allCpvCodes) {
      return [];
    }
    return selectedCpvCodes.map((v) => {
      const code = findCpvCode(allCpvCodes, v);
      return {
        label: code ? `${code.code}: ${code.name}` : v,
        value: v,
      };
    });
  }, [selectedCpvCodes, allCpvCodes]);

  const removeCpvCode = useCallback(
    (removedValue: string) => {
      const prefix = getCpvPrefix(removedValue);
      const codesToRemove = selectedCpvCodes.filter((code) => code.startsWith(prefix));
      onChange(selectedCpvCodes.filter((v) => !codesToRemove.includes(v)));
    },
    [onChange, selectedCpvCodes],
  );

  // The search tree only renders the filtered nodes, so its checked set omits
  // selections outside the current search. Reconcile removals against the
  // filtered subset before merging back into the full selection.
  const onFilteredChange = useCallback(
    (newCpvCodes: string[]) => {
      const removedCpvCodes: string[] = [];
      for (const code of selectedCpvCodes) {
        if (filteredCpvCodes.some((f) => f.value === code) && !newCpvCodes.includes(code)) {
          removedCpvCodes.push(code);
        }
      }
      const filteredSelectedCpvs = selectedCpvCodes.filter((v) => !removedCpvCodes.includes(v));
      onChange(simpleArrayDedupe([...filteredSelectedCpvs, ...newCpvCodes]));
    },
    [filteredCpvCodes, onChange, selectedCpvCodes],
  );

  const isSearching = debouncedText.length > 0;

  return (
    <Container column gap={24}>
      <Wrapper
        aria-label="cpvCodeSelect"
        onBlur={(e) => {
          if (e.currentTarget.contains(e.relatedTarget as Node)) {
            return;
          }
          setIsOpen(false);
        }}
      >
        {/* Selected codes render as tags inside the input, alongside the search
            field — consistent with the keyword/supplier inputs. */}
        <InputBox onClick={() => setIsOpen(true)} aria-label="selections">
          {displayedCpvTags.map((tag) => {
            const count = resultCounts?.[tag.value];
            return (
              <Tag
                key={tag.value}
                ariaLabel={tag.value}
                label={
                  <Flex gap={4}>
                    {tag.label}
                    <SignalMatchCount count={count} isLoadingCounts={isLoadingCounts} />
                  </Flex>
                }
                tooltip={countTooltip(count)}
                icon={<UIcon icon="xClose" size={12} />}
                onClick={() => removeCpvCode(tag.value)}
              />
            );
          })}
          <BareInput
            aria-label="cpvSearchInput"
            value={searchText}
            placeholder={
              displayedCpvTags.length === 0 ? "Search for CPV code or description" : undefined
            }
            onChange={(e) => {
              setSearchText(e.currentTarget.value.trim());
              setIsOpen(true);
            }}
          />
        </InputBox>
        {isOpen && (
          // preventDefault on mousedown keeps focus on the input so selecting a
          // tree node doesn't blur-close the popup before the check registers.
          <Popup onMouseDown={(e) => e.preventDefault()} aria-label="cpvDropdown">
            <CpvTreeSelect
              value={selectedCpvCodes}
              onChange={isSearching ? onFilteredChange : onChange}
              cpvTree={isSearching ? filteredCpvCodes : cpvTree}
              isLoading={isLoading}
              expand={isSearching}
            />
          </Popup>
        )}
      </Wrapper>
      <ClearAllSignalButton name={name} control={control} />
    </Container>
  );
}

const Container = styled(Flex)({
  marginTop: 24,
});

const Wrapper = styled.div({
  position: "relative",
});

// Rendered in normal flow (not absolutely positioned) so the surrounding
// Collapsible grows to fit it rather than clipping it; the tree scrolls within.
const Popup = styled.div(({ theme }) => ({
  marginTop: 4,
  maxHeight: 400,
  overflowY: "auto",
  padding: 8,
  backgroundColor: theme.colors.sysBackgroundDefault,
  border: `1px solid ${theme.colors.sysBorderDecorative}`,
  borderRadius: 4,
  boxShadow:
    "0px 3px 6px -4px rgba(0, 0, 0, 0.12), 0px 6px 16px rgba(0, 0, 0, 0.08), 0px 9px 28px 8px rgba(0, 0, 0, 0.05)",
}));

// Input-styled container that holds the selected tags and the search field
// together, mirroring the keyword/supplier inputs.
const InputBox = styled.div(({ theme }) => ({
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  gap: 8,
  padding: "4px 8px",
  minHeight: 32,
  cursor: "text",
  backgroundColor: theme.colors.sysBackgroundDefault,
  border: `1px solid ${theme.colors.sysBorderFunctional}`,
  borderRadius: 4,
  "&:focus-within": {
    borderColor: theme.colors.sysPrimaryDefault,
  },
}));

const BareInput = styled.input({
  flex: "1 1 120px",
  minWidth: 120,
  border: "none",
  outline: "none",
  background: "transparent",
  padding: 0,
  minHeight: 24,
});
